import { Grid } from '@mui/material';
import { BasicButton } from '@tassat/button';
import {
    ActionTypes,
    STATE_PROVINCE_REQUIRED_COUNTRIES,
    getCountryOptions,
    getStateProvinceOptions
} from '@tassat/common-definitions';
import {
    CheckboxField,
    SelectAssetsField,
    SelectField,
    SimpleErrorMessage,
    TextField
} from '@tassat/formik-input';
import * as React from 'react';

import { IFormContentsProps } from './types';

export const FirmFormContents: React.FC<IFormContentsProps> = (props: IFormContentsProps) => {
    const {
        classes,
        values,
        errors,
        editFirm,
        actionType,
        features,
        isSubmitting,
        submitError,
        isValid,
        dirty,
        submitForm,
        setFieldTouched,
        renderButton,
        availableAssetsMapped,
        secondaryApiConnectionExists
    } = props;

    const countryCode = values.Country?.value as string | undefined;
    const isStateProvinceRequired = !!countryCode && STATE_PROVINCE_REQUIRED_COUNTRIES.includes(countryCode);

    React.useEffect(() => {
        setFieldTouched('IsTokenAPIFirm', true);
        setFieldTouched('IsIBNEnabled', true);
        setFieldTouched('IsCollateral', true);

        if (actionType === ActionTypes.edit) {
            setFieldTouched('Country', true);
            setFieldTouched('State', true);
            setFieldTouched('AssetMapping', true);
        }
    }, []);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <Grid
                    className={classes.fullWidth}
                    container
                    item
                    justifyContent='space-between'
                    spacing={2}
                    xs={12}
                >
                    <Grid
                        item
                        xs={6}
                    >
                        <TextField
                            disabled={editFirm?.IsClosed}
                            errors={errors}
                            label='Firm name'
                            maxLength={48}
                            name='Name'
                            placeholder='Firm Name'
                            testId='FirmName'
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={6}
                    >
                        <TextField
                            disabled={editFirm?.IsClosed}
                            errors={errors}
                            label='Notification Email'
                            maxLength={64}
                            name='FirmEmail'
                            placeholder='Email'
                            testId='FirmEmail'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    className={classes.fullWidth}
                    container
                    item
                    spacing={2}
                    xs={12}
                >
                    <Grid
                        item
                        xs={6}
                    >
                        <TextField
                            disabled={editFirm?.IsClosed}
                            errors={errors}
                            label='Legal Entity Identifier "LEI"'
                            maxLength={20}
                            name='Lei'
                            placeholder='Legal Entity Identifier'
                            testId='Lei'
                            values={values}
                        />
                    </Grid>
                    {features?.SSO && features.SSOConfig?.CompanyCodeRequired && (
                        <Grid
                            item
                            xs={6}
                        >
                            <TextField
                                disabled={editFirm?.IsClosed}
                                errors={errors}
                                label='Company Code'
                                maxLength={32}
                                name='CompanyCode'
                                placeholder='Company Code'
                                testId='CompanyCode'
                                values={values}
                            />
                        </Grid>
                    )}
                    <Grid
                        item
                        xs={6}
                    >
                        <TextField
                            disabled={editFirm?.IsClosed}
                            errors={errors}
                            label='Notes'
                            maxLength={50}
                            name='Notes'
                            placeholder='Notes'
                            testId='Notes'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    className={classes.fullWidth}
                    container
                    item
                    spacing={2}
                    xs={12}
                >
                    <Grid
                        item
                        xs={6}
                    >
                        <SelectField
                            autoComplete={true}
                            disabled={editFirm?.IsClosed}
                            errors={errors}
                            id='input_CountryDropdown'
                            label='Country'
                            name='Country'
                            options={getCountryOptions()}
                            placeholder='Country'
                            updateValuesOnChange={() => ({ State: undefined })}
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={6}
                    >
                        <SelectField
                            autoComplete={true}
                            disabled={editFirm?.IsClosed || !isStateProvinceRequired}
                            errors={errors}
                            id='input_StateDropdown'
                            key={countryCode}
                            label='State/Province'
                            name='State'
                            options={getStateProvinceOptions(countryCode)}
                            placeholder='State/Province'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    className={classes.fullWidth}
                    container
                    item
                    justifyContent='space-between'
                    spacing={2}
                    xs={12}
                >
                    <Grid
                        item
                        xs={12}
                    >
                        <SelectAssetsField
                            autoComplete
                            disabled={editFirm?.IsClosed}
                            errors={errors}
                            label='Asset Permissions'
                            name='AssetMapping'
                            options={availableAssetsMapped || []}
                            placeholder='Type in asset name or click the arrow for more options'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    className={classes.fullWidth}
                    container
                    item
                    spacing={2}
                    xs={12}
                >
                    <Grid
                        container
                        item
                        justifyContent='flex-start'
                        xs={6}
                    >
                        <Grid
                            className={classes.checkboxField}
                            item
                        >
                            <CheckboxField
                                disabled={editFirm?.IsClosed}
                                errors={errors}
                                label='Token API'
                                name='IsTokenAPIFirm'
                                values={values}
                            />
                        </Grid>
                        {features?.ibnEnabled && (
                            <Grid
                                className={classes.checkboxField}
                                item
                            >
                                <CheckboxField
                                    disabled={editFirm?.IsClosed}
                                    errors={errors}
                                    label='The Network'
                                    name='IsIBNEnabled'
                                    values={values}
                                />
                            </Grid>
                        )}
                        {features?.fireblocks.api && secondaryApiConnectionExists && (
                            // TODO Update in the future to handle multiple API connections
                            <Grid
                                className={classes.checkboxField}
                                item
                            >
                                <CheckboxField
                                    disabled={editFirm?.IsClosed}
                                    errors={errors}
                                    label='Fireblocks API'
                                    name='APISecondaryConnectionType'
                                    values={values}
                                />
                            </Grid>
                        )}
                        {features?.liquidityFacility && (
                            <Grid
                                className={classes.checkboxField}
                                item
                            >
                                <CheckboxField
                                    disabled={editFirm?.IsClosed}
                                    errors={errors}
                                    label='Liquidity Facility'
                                    name='IsLiquidityFacility'
                                    values={values}
                                />
                            </Grid>
                        )}
                        <Grid
                            className={`${classes.checkboxField} ${classes.checkboxFieldLong}`}
                            item
                        >
                            <CheckboxField
                                disabled={editFirm?.IsClosed}
                                errors={errors}
                                label='Collateral Management'
                                name='IsCollateral'
                                values={values}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    alignItems='center'
                    className={classes.submitButtonContainer}
                    container
                    item
                    justifyContent='space-evenly'
                    xs={12}
                >
                    {renderButton()}
                    <Grid
                        className={classes.submitButton}
                        container
                        direction='column'
                        item
                    >
                        <BasicButton
                            disabled={!dirty ||
                                isSubmitting ||
                                !isValid ||
                                editFirm?.IsClosed}
                            onClick={submitForm}
                            testId='submit-firm-form'
                            text='Submit'
                            type='submit'
                        />
                        <SimpleErrorMessage
                            className={classes.submitError}
                            error={submitError}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};